import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Mic, Users } from 'lucide-react';
import { useNavigation } from '../../contexts/NavigationContext';
import { useStoryStore } from '../../store/storyStore';
import { useAuthStore } from '../../store/authStore';

interface MobileBottomNavProps {
  onOpenStoryPicker: () => void;
  onOpenFamilyManagement: () => void;
}

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
  onOpenStoryPicker,
  onOpenFamilyManagement
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isRecordingMode, lastActiveStoryId } = useNavigation();
  const { stories } = useStoryStore();
  const { user } = useAuthStore();

  if (!user || isRecordingMode) return null;

  const isOnDashboard = location.pathname === '/dashboard';
  const isOnStoryRecorder = location.pathname.startsWith('/story-recorder');

  const getIncompleteStories = () => {
    return stories.filter(s => s.progress < 100);
  };

  const getRecordLabel = () => {
    const incompleteStories = getIncompleteStories();
    if (incompleteStories.length === 0) return 'Grabar';
    return 'Seguir grabando';
  };

  const handleRecordClick = () => {
    const incompleteStories = getIncompleteStories();

    if (isOnStoryRecorder) {
      window.dispatchEvent(new CustomEvent('openRecordingOverlay'));
      return;
    }

    if (incompleteStories.length === 0) {
      onOpenStoryPicker();
      return;
    }

    onOpenStoryPicker();
  };

  const navItems = [
    {
      id: 'home',
      icon: Home,
      label: 'Inicio',
      isActive: isOnDashboard,
      onClick: () => navigate('/dashboard')
    },
    {
      id: 'record',
      icon: Mic,
      label: getRecordLabel(),
      isActive: false,
      onClick: handleRecordClick,
      isPrimary: true
    },
    {
      id: 'family',
      icon: Users,
      label: 'Familia',
      isActive: false,
      onClick: onOpenFamilyManagement
    }
  ];

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-sticky bg-white border-t border-stone-200 shadow-lg">
      <div
        className="flex items-center justify-around h-16"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {navItems.map((item) => {
          const Icon = item.icon;

          if (item.isPrimary) {
            return (
              <button
                key={item.id}
                id="mobile-record-btn"
                onClick={item.onClick}
                className="relative -mt-6 flex flex-col items-center justify-center"
              >
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-rose-400 to-rose-500 shadow-lg flex items-center justify-center text-white transform active:scale-95 transition-transform animate-[subtle-pulse_3s_ease-in-out_infinite]">
                  <Icon className="h-6 w-6" />
                </div>
                <span className="text-[10px] font-medium text-rose-600 mt-1">
                  {item.label}
                </span>
              </button>
            );
          }

          return (
            <button
              key={item.id}
              onClick={item.onClick}
              className={`flex flex-col items-center justify-center py-2 px-4 transition-colors ${
                item.isActive
                  ? 'text-rose-600'
                  : 'text-stone-500 active:text-stone-700'
              }`}
            >
              <Icon className={`h-5 w-5 ${item.isActive ? 'stroke-[2.5px]' : ''}`} />
              <span className={`text-[10px] mt-1 ${item.isActive ? 'font-semibold' : 'font-medium'}`}>
                {item.label}
              </span>
              {item.isActive && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-rose-500 rounded-full" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
